import React, { useState } from "react";

import {
  Box,
  Button,
  Collapse,
  Container,
  Dialog,
  DialogTitle,
  FormControl,
  Input,
  InputAdornment,
  InputLabel,
  makeStyles,
  MenuItem,
  Select,
  Theme,
  Typography
} from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import { RootState } from "../../app/rootReducer";
import { syncQuote } from "../../store/QuoteSlice";
import { Deduction } from "../../api/Deduction/Deduction";
import { Quote } from "../../api/Quote/Quote";
import { cleanNumberInput, deductionValue } from "../../app/utils";
import { toast } from "react-toastify";
import { addDeductionToQuote } from "../../api/Deduction/DeductionApi";
import { EQuotePurchaserType, EQuoteType } from "../../api/Types";
import { SolarBusinessAgriVatRate } from "../../app/consts";

const useStyles = makeStyles((theme: Theme) => ({
  margin: {
    margin: theme.spacing(1)
  },
  formControl: {
    margin: theme.spacing(1),
    minWidth: 120
  },
  button: {
    margin: theme.spacing(1)
  }
}));

interface Props {
  isOpen: boolean;
  onClose: () => void;
  quote: Quote;
}

export const DeductionModal = (props: Props) => {
  const dispatch = useDispatch();
  const classes = useStyles();

  const { deductionsById } = useSelector((state: RootState) => state.deductions);

  const [selectedDeduction, setSelectedDeduction] = useState<Deduction | null>(null);
  const [inputValue, setInputValue] = useState<string>("");
  const [discountValue, setDiscountValue] = useState<number>(0);

  const changePrice = function (input: string) {
    var value = cleanNumberInput(input);
    var newPrice: number = Number(value.endsWith(".") ? `${value}0` : value);

    if (newPrice > 0) {
      newPrice *= -1;
      value = `-${value}`;
    }

    setInputValue(value);
    setDiscountValue(newPrice);
  };

  const handleSave = async () => {
    if (!selectedDeduction) {
      return;
    }

    let newDiscountValue = Math.abs(Number(discountValue));

    if (props.quote.type === EQuoteType.Solar && props.quote.purchaserType !== EQuotePurchaserType.Home) {
      newDiscountValue += newDiscountValue * (SolarBusinessAgriVatRate / 100);
    }

    await addDeductionToQuote(props.quote, selectedDeduction, newDiscountValue)
      .then(() => {
        dispatch(syncQuote(props.quote.id, false));
      })
      .catch((err) => {
        console.log(err);
        toast.error(err);
      });
  };

  const handleSelectDiscount = (event: React.ChangeEvent<{ value: unknown }>) => {
    var deductionId = event.target.value as string;

    if (deductionId) {
      const deduction = deductionsById[deductionId];
      setSelectedDeduction(deduction);
      setDiscountValue(deductionValue(deduction));
      setInputValue(String(deductionValue(deduction)));
    } else {
      setSelectedDeduction(null);
      setDiscountValue(0);
      setInputValue("");
    }
  };

  return (
    <Dialog
      open={props.isOpen}
      onClose={props.onClose}
      aria-labelledby="deduction-selection"
      aria-describedby="deduction-select"
    >
      <DialogTitle id="select-discount">Apply Discount</DialogTitle>
      <Container maxWidth="md">
        <Typography variant="body2" paragraph>
          Select a discount to apply to this quote. The same discount can not be applied more than once.
        </Typography>
        <Box pb={3} minWidth="450px">
          <FormControl className={classes.formControl}>
            <InputLabel id="select-discount-label">Discount</InputLabel>
            <Select
              labelId="select-discount-label"
              id="select-discount-select"
              onChange={handleSelectDiscount}
              value={(selectedDeduction && selectedDeduction.id) || ""}
            >
              {Object.values(deductionsById).map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Collapse in={(selectedDeduction && selectedDeduction.isCustomValue) || false}>
            <FormControl fullWidth className={classes.margin}>
              <InputLabel htmlFor="standard-adornment-amount">Amount</InputLabel>
              <Input
                id="standard-adornment-amount"
                value={inputValue}
                onChange={(e) => {
                  changePrice(e.target.value);
                }}
                startAdornment={<InputAdornment position="start">€</InputAdornment>}
              />
            </FormControl>
          </Collapse>
          <Box display="flex" justifyContent="center">
            <Button variant="contained" onClick={(_) => props.onClose()} className={classes.button}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="secondary"
              onClick={(_) => handleSave()}
              disabled={selectedDeduction === null}
              className={classes.button}
            >
              Apply Discount
            </Button>
          </Box>
        </Box>
      </Container>
    </Dialog>
  );
};
